import UpgradeBanner from "../components/UpgradeBanner";
import LoadingMessages from "../components/LoadingMessages";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuthStore } from "../store/authStore";
import { toast } from "sonner";
import {
  Gauge, MagnifyingGlass, CheckCircle,
  WarningCircle, DownloadSimple, FileSql, FilePdf,
  ClockCounterClockwise, ArrowSquareOut
} from "@phosphor-icons/react";

/* ─── Design tokens ─────────────────────────────────────────── */
const C = {
  bg:       '#0a0a0f',
  surface:  '#13131f',
  surfaceHi:'#181828',
  input:    '#1a1a2e',
  border:   'rgba(124,58,237,0.2)',
  borderHi: 'rgba(124,58,237,0.45)',
  violet:   '#7c3aed',
  blue:     '#3b82f6',
  text:     '#f1f5f9',
  muted:    '#94a3b8',
  faint:    '#475569',
  gradient: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
  shadow:   '0 4px 24px rgba(124,58,237,0.1)',
  font:     "'Inter', system-ui, -apple-system, sans-serif",
};

const card = {
  background: C.surface,
  border: `1px solid ${C.border}`,
  borderRadius: 16,
  padding: '24px 26px',
  marginBottom: 16,
  boxShadow: C.shadow,
};

const TYPE_LABELS = {
  title_rewrite:     "Title rewrites",
  slug_optimize:     "Slug optimizations",
  duplicate_content: "Duplicate content",
};

/* ─── Sub-components ─────────────────────────────────────────── */
function SpinnerIcon() {
  return <div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.25)', borderTop: '2px solid #fff', borderRadius: '50%', display: 'inline-block' }} className="animate-spin" />;
}

function SeverityBadge({ severity }) {
  const styles = {
    critical: { bg: 'rgba(239,68,68,0.15)',  color: '#f87171',  border: 'rgba(239,68,68,0.3)'  },
    high:     { bg: 'rgba(245,158,11,0.15)', color: '#fbbf24',  border: 'rgba(245,158,11,0.3)' },
    medium:   { bg: 'rgba(234,179,8,0.15)',  color: '#fde047',  border: 'rgba(234,179,8,0.25)' },
    low:      { bg: 'rgba(100,116,139,0.15)',color: '#94a3b8',  border: 'rgba(100,116,139,0.3)'},
  };
  const s = styles[severity] || styles.low;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 5, background: s.bg, color: s.color, border: `1px solid ${s.border}`, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
      {severity}
    </span>
  );
}

function ProgressBar({ progress, label }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.muted, marginBottom: 8 }}>
        <span>{label}</span>
        <span style={{ fontWeight: 700, color: C.violet }}>{progress}%</span>
      </div>
      <div style={{ height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ height: '100%', borderRadius: 99, background: C.gradient, width: `${progress}%`, transition: 'width 0.5s ease', boxShadow: '0 0 12px rgba(124,58,237,0.5)' }} />
      </div>
    </div>
  );
}

const WAITING_MESSAGES = [
  { emoji: "☕", text: "Grab a coffee — this may take a few minutes" },
  { emoji: "🍵", text: "Time to brew some tea while your audit runs" },
  { emoji: "🔍", text: "Scanning every page of your site thoroughly" },
  { emoji: "📊", text: "Deep SEO analysis in progress" },
  { emoji: "⚡", text: "Finding opportunities to improve your rankings" },
];

function WaitingMessage() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % WAITING_MESSAGES.length), 7000);
    return () => clearInterval(t);
  }, []);
  const msg = WAITING_MESSAGES[idx];
  return (
    <div style={{ textAlign: 'center', padding: '24px 20px', background: 'rgba(124,58,237,0.06)', border: `1px solid rgba(124,58,237,0.12)`, borderRadius: 12, marginTop: 16 }}>
      <div style={{ fontSize: 32, marginBottom: 10 }}>{msg.emoji}</div>
      <p style={{ fontSize: 13, fontWeight: 600, color: C.muted, margin: '0 0 6px' }}>{msg.text}</p>
      <p style={{ fontSize: 11, color: C.faint, margin: 0 }}>Don't close this page · Analysis continues in background</p>
    </div>
  );
}

function CorrectionCard({ correction, onCSV, onSQL }) {
  const borderMap = { critical: 'rgba(239,68,68,0.3)', high: 'rgba(245,158,11,0.3)', medium: 'rgba(234,179,8,0.2)', low: C.border };
  const border = borderMap[correction.severity] || C.border;
  return (
    <div style={{ background: C.surfaceHi, border: `1px solid ${border}`, borderRadius: 10, padding: '14px 16px', marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <SeverityBadge severity={correction.severity} />
            <span style={{ fontSize: 11, color: C.faint, fontFamily: 'monospace' }}>{correction.type?.replace(/_/g, ' ')}</span>
            {correction.score_impact && (
              <span style={{ fontSize: 11, fontWeight: 700, color: '#10b981' }}>+{correction.score_impact} pts</span>
            )}
          </div>
          <p style={{ fontSize: 12, color: C.faint, margin: '0 0 4px', textDecoration: 'line-through', lineHeight: 1.5 }}>{correction.current}</p>
          <p style={{ fontSize: 13, fontWeight: 600, color: C.text, margin: '0 0 6px', lineHeight: 1.5 }}>→ {correction.suggested}</p>
          <p style={{ fontSize: 11, color: C.muted, margin: 0, lineHeight: 1.5 }}>{correction.rationale}</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
          <button onClick={() => onCSV(correction, 'single')}
            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', background: 'rgba(16,185,129,0.12)', color: '#10b981', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: C.font }}>
            <DownloadSimple style={{ width: 11, height: 11 }} /> CSV
          </button>
          <button onClick={() => onSQL(correction)}
            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', background: 'rgba(124,58,237,0.12)', color: '#a78bfa', border: '1px solid rgba(124,58,237,0.25)', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: C.font }}>
            <FileSql style={{ width: 11, height: 11 }} /> SQL
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SeoActionCenter
═══════════════════════════════════════════════════════════════ */
export default function SeoActionCenter() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [url, setUrl]                     = useState("");
  const [manualSitemap, setManualSitemap] = useState("");
  const [useManual, setUseManual]         = useState(false);
  const [loading, setLoading]             = useState(false);
  const [jobId, setJobId]                 = useState(null);
  const [status, setStatus]               = useState(null);
  const [results, setResults]             = useState(null);
  const [polling, setPolling]             = useState(false);
  const [error, setError]                 = useState(null);
  const [auditHistory, setAuditHistory]   = useState([]);
  const [showHistory, setShowHistory]     = useState(false);

  useEffect(() => {
    apiClient.get("/traffic/audit/history-disabled?limit=10")
      .then(h => setAuditHistory(h.audits || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!polling || !jobId) return;
    const interval = setInterval(async () => {
      try {
        const s = await apiClient.get(`/traffic/audit/status/${jobId}`);
        setStatus(s);
        if (s.status === 'completed') {
          clearInterval(interval);
          setPolling(false);
          const r = await apiClient.get(`/traffic/audit/results/${jobId}`);
          setResults(r);
          toast.success(`Audit complete! ${r.total_pages} pages analyzed.`);
        } else if (s.status === 'failed') {
          clearInterval(interval);
          setPolling(false);
          setError({ type: 'blocked', message: s.error || "Audit failed", suggestion: "Try entering your sitemap URL manually below." });
        }
      } catch (e) { console.error(e); }
    }, 3000);
    return () => clearInterval(interval);
  }, [polling, jobId]);

  const startAudit = async () => {
    let targetUrl = url.trim();
    let sitemapUrl = manualSitemap.trim();
    if (!targetUrl && !sitemapUrl) { toast.error("Please enter a URL"); return; }
    if (useManual && !targetUrl && sitemapUrl) {
      try { targetUrl = new URL(sitemapUrl).origin; }
      catch { targetUrl = sitemapUrl.split('/').slice(0, 3).join('/'); }
    }
    setLoading(true); setResults(null); setError(null);
    try {
      const response = useManual && sitemapUrl
        ? await apiClient.post("/traffic/audit/site", { url: targetUrl, sitemap_urls: [sitemapUrl], max_pages: 500 })
        : await apiClient.post("/traffic/audit/site", { url: targetUrl, max_pages: 500 });
      setJobId(response.job_id);
      setPolling(true);
      setLoading(false);
      toast.success("Audit started! This may take a few minutes...");
    } catch (e) {
      const detail = e?.detail || null;
      if (detail?.error === "plan_limit") {
        setError({ type: "plan_limit", message: detail.message || "Free plan: 1 audit per day.", suggestion: "Upgrade to Pro for unlimited audits." });
      } else {
        toast.error(typeof e?.message === "string" ? e.message : "Failed to start audit");
      }
      setLoading(false);
    }
  };

  const exportCSV = (correction = null, type = 'all') => {
    const data = type === 'all' ? (results?.corrections || []) : [correction];
    const headers = ['Type', 'Severity', 'Current', 'Suggested', 'Reason'];
    const rows = data.map(c => [c.type, c.severity, c.current, c.suggested, c.rationale]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob(["﻿" + csv], { type: 'text/csv;charset=utf-8;' }));
    link.download = `seo_corrections_${jobId || 'audit'}.csv`;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    toast.success(`${data.length} correction(s) exported to CSV`);
  };

  const exportSQL = async () => {
    if (!jobId && !results) return;
    try {
      const response = await apiClient.fetch(`/traffic/export/sql`, { method: 'POST', body: JSON.stringify({ job_id: jobId || results?.job_id }) });
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `seo_corrections_${jobId || results?.job_id}.sql`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      toast.success("SQL export generated");
    } catch { toast.error("SQL export failed"); }
  };

  const exportPDF = async () => {
    if (!jobId && !results) return;
    try {
      const response = await apiClient.fetch(`/traffic/export/pdf`, { method: 'POST', body: JSON.stringify({ job_id: jobId || results?.job_id }) });
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `seo_report_${jobId || results?.job_id}.pdf`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      toast.success("PDF report generated");
    } catch { toast.error("PDF export failed"); }
  };

  const correctionsByType = results?.corrections?.reduce((acc, c) => {
    acc[c.type] = acc[c.type] || []; acc[c.type].push(c); return acc;
  }, {}) || {};

  const progressPct   = status?.status === 'detecting' ? 15 : status?.status === 'crawling' ? 40 : status?.status === 'analyzing' ? 70 : 90;
  const progressLabel = status?.status === 'detecting' ? "🔍 Discovering sitemaps…" : status?.status === 'crawling' ? "🕷️ Crawling pages…" : status?.status === 'analyzing' ? "📊 Analyzing SEO content…" : "⚙️ Processing…";

  /* ─── Render ──────────────────────────────────────────────── */
  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: C.font, color: C.text }}>
      <main style={{ maxWidth: 960, margin: '0 auto', padding: '40px 28px 80px' }}>

        <UpgradeBanner feature="Analyses illimitées, export PDF, historique 1 an" />

        {/* Page header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 32 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{ width: 36, height: 36, background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Gauge weight="bold" style={{ width: 17, height: 17, color: '#f59e0b' }} />
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Site Audit</span>
            </div>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: C.text, margin: '0 0 6px', letterSpacing: '-0.8px' }}>Action Center</h1>
            <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>Scan your entire site and get actionable SEO corrections — titles, slugs, duplicates.</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {user && (
              <span style={{ fontSize: 10, fontWeight: 800, padding: '4px 10px', borderRadius: 6, background: 'rgba(124,58,237,0.12)', color: '#a78bfa', border: '1px solid rgba(124,58,237,0.25)', letterSpacing: '0.08em', textTransform: 'uppercase', alignSelf: 'center' }}>
                {user.plan || 'free'}
              </span>
            )}
            <button onClick={() => setShowHistory(!showHistory)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: showHistory ? C.gradient : 'transparent', border: `1px solid ${showHistory ? 'transparent' : C.border}`, borderRadius: 8, fontSize: 12, fontWeight: 600, color: showHistory ? '#fff' : C.muted, cursor: 'pointer', fontFamily: C.font, transition: 'all 0.15s' }}>
              <ClockCounterClockwise style={{ width: 13, height: 13 }} /> History
            </button>
          </div>
        </div>

        {/* History */}
        {showHistory && auditHistory.length > 0 && (
          <div style={{ ...card, marginBottom: 20 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: C.faint, margin: '0 0 14px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Audit history</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {auditHistory.map((a, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: '10px 14px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 600, color: C.text, margin: '0 0 2px' }}>{a.url}</p>
                    <p style={{ fontSize: 11, color: C.faint, margin: 0 }}>{new Date(a.created_at).toLocaleString('en', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: 16, fontWeight: 800, color: C.violet, margin: '0 0 1px' }}>{a.total_corrections} fixes</p>
                    <p style={{ fontSize: 11, color: C.faint, margin: 0 }}>{a.total_pages} pages</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Audit form */}
        <div style={{ ...card }}>
          <p style={{ fontSize: 14, fontWeight: 700, color: C.text, margin: '0 0 4px' }}>Audit your site</p>
          <p style={{ fontSize: 12, color: C.muted, margin: '0 0 18px' }}>Enter your website URL to automatically detect SEO issues across all pages.</p>

          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <MagnifyingGlass style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: C.faint }} />
              <input type="text" value={url} onChange={e => setUrl(e.target.value)}
                placeholder="https://yoursite.com"
                onKeyDown={e => e.key === 'Enter' && startAudit()}
                disabled={loading || polling || useManual}
                style={{ width: '100%', background: C.input, border: `1px solid rgba(124,58,237,0.3)`, borderRadius: 10, padding: '11px 14px 11px 40px', fontSize: 14, color: C.text, outline: 'none', boxSizing: 'border-box', fontFamily: C.font, opacity: useManual ? 0.5 : 1, transition: 'border-color 0.2s' }}
                onFocus={e => e.target.style.borderColor = C.violet}
                onBlur={e => e.target.style.borderColor = 'rgba(124,58,237,0.3)'}
              />
            </div>
            <button onClick={startAudit}
              disabled={loading || polling || (useManual ? !manualSitemap.trim() : !url.trim())}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 22px', background: (loading || polling) ? C.faint : C.gradient, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: (loading || polling) ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', fontFamily: C.font, boxShadow: (!loading && !polling) ? '0 4px 16px rgba(124,58,237,0.3)' : 'none', transition: 'all 0.15s' }}>
              {(loading || polling) ? <SpinnerIcon /> : <MagnifyingGlass weight="bold" style={{ width: 14, height: 14 }} />}
              {(loading || polling) ? "Analyzing…" : "Launch audit"}
            </button>
          </div>

          <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 14 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: useManual ? 12 : 0 }}>
              <input type="checkbox" checked={useManual} onChange={e => setUseManual(e.target.checked)}
                style={{ width: 14, height: 14, accentColor: C.violet }} />
              <span style={{ fontSize: 12, color: C.muted }}>Site blocks crawlers? Enter your sitemap URL manually</span>
            </label>
            {useManual && (
              <div>
                <input type="text" value={manualSitemap} onChange={e => setManualSitemap(e.target.value)}
                  placeholder="https://yoursite.com/sitemap.xml" autoFocus
                  style={{ width: '100%', background: C.input, border: `1px solid ${C.violet}`, borderRadius: 10, padding: '11px 14px', fontSize: 14, color: C.text, outline: 'none', boxSizing: 'border-box', fontFamily: C.font }}
                />
                <p style={{ fontSize: 11, color: C.faint, margin: '6px 0 0' }}>e.g. https://yoursite.com/sitemap.xml</p>
              </div>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{ background: error.type === 'plan_limit' ? 'rgba(124,58,237,0.08)' : 'rgba(239,68,68,0.08)', border: `1px solid ${error.type === 'plan_limit' ? 'rgba(124,58,237,0.3)' : 'rgba(239,68,68,0.3)'}`, borderRadius: 12, padding: '16px 20px', marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 10 }}>
              <WarningCircle weight="fill" style={{ width: 18, height: 18, color: error.type === 'plan_limit' ? '#a78bfa' : '#f87171', flexShrink: 0, marginTop: 1 }} />
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: error.type === 'plan_limit' ? '#c4b5fd' : '#f87171', margin: '0 0 4px' }}>
                  {error.type === 'plan_limit' ? 'Plan limit reached' : 'Access error'}
                </p>
                <p style={{ fontSize: 12, color: C.muted, margin: '0 0 8px' }}>{error.message}</p>
                {error.type === 'plan_limit'
                  ? <a href="/pricing" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 16px', background: C.gradient, color: '#fff', borderRadius: 8, fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>Upgrade to Pro →</a>
                  : <p style={{ fontSize: 11, color: C.faint, margin: 0 }}>{error.suggestion}</p>
                }
              </div>
            </div>
          </div>
        )}

        {/* Progress */}
        {polling && status && (
          <div style={{ ...card }}>
            <p style={{ fontSize: 14, fontWeight: 700, color: C.text, margin: '0 0 16px' }}>Analysis in progress</p>
            <ProgressBar progress={progressPct} label={progressLabel} />
            {status.pages_found && <p style={{ fontSize: 12, color: C.muted, margin: '10px 0 0' }}>📄 {status.pages_found} pages found</p>}
            <LoadingMessages />
          </div>
        )}

        {/* Free plan limit banner */}
        {results?.is_limited && (
          <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 12, padding: '12px 18px', marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16 }}>⚡</span>
              <div>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#fbbf24', margin: 0 }}>Free plan — {results.total_pages} pages analyzed (max {results.max_pages_limit})</p>
                <p style={{ fontSize: 11, color: C.muted, margin: '2px 0 0' }}>Upgrade to Pro to audit up to 500 pages.</p>
              </div>
            </div>
            <a href="/pricing" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 16px', background: C.gradient, color: '#fff', borderRadius: 8, fontSize: 12, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>
              Upgrade to Pro →
            </a>
          </div>
        )}

        {/* Results */}
        {results?.corrections?.length > 0 && (
          <div>
            {/* Summary card */}
            <div style={{ ...card }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
                <p style={{ fontSize: 15, fontWeight: 700, color: C.text, margin: 0 }}>Audit results</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[
                    { label: 'CSV', fn: () => exportCSV(), color: '#10b981', border: 'rgba(16,185,129,0.3)', icon: <DownloadSimple style={{ width: 11, height: 11 }} /> },
                    { label: 'SQL', fn: () => exportSQL(),  color: '#a78bfa',  border: 'rgba(124,58,237,0.3)',  icon: <FileSql style={{ width: 11, height: 11 }} /> },
                    { label: 'PDF', fn: exportPDF,          color: '#f87171',  border: 'rgba(239,68,68,0.3)',   icon: <FilePdf style={{ width: 11, height: 11 }} /> },
                  ].map(b => (
                    <button key={b.label} onClick={b.fn}
                      style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 13px', background: `${b.color}15`, color: b.color, border: `1px solid ${b.border}`, borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: C.font }}>
                      {b.icon} {b.label}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                {[
                  { label: 'Pages analyzed',  value: results.total_pages,                            color: C.text   },
                  { label: 'Total fixes',     value: results.summary?.total_corrections || 0,         color: C.violet },
                  { label: 'Critical',        value: results.summary?.by_severity?.critical || 0,     color: '#f87171'},
                  { label: 'Title rewrites',  value: results.summary?.by_type?.title_rewrite || 0,    color: '#10b981'},
                ].map(s => (
                  <div key={s.label} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '16px', textAlign: 'center' }}>
                    <p style={{ fontSize: 28, fontWeight: 900, color: s.color, margin: 0, letterSpacing: '-0.8px' }}>{s.value}</p>
                    <p style={{ fontSize: 11, color: C.faint, margin: '4px 0 0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Corrections by type */}
            {Object.entries(correctionsByType).map(([type, corrections]) => (
              <div key={type} style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <div style={{ width: 7, height: 7, background: C.gradient, borderRadius: '50%' }} />
                  <p style={{ fontSize: 14, fontWeight: 700, color: C.text, margin: 0 }}>
                    {TYPE_LABELS[type] || type}
                    <span style={{ fontSize: 12, fontWeight: 400, color: C.faint, marginLeft: 6 }}>({corrections.length})</span>
                  </p>
                </div>
                {corrections.map((c, i) => (
                  <CorrectionCard key={i} correction={c} onCSV={exportCSV} onSQL={exportSQL} />
                ))}
              </div>
            ))}
          </div>
        )}

        {/* No issues */}
        {results?.corrections?.length === 0 && (
          <div style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 16, padding: '48px 32px', textAlign: 'center' }}>
            <CheckCircle weight="fill" style={{ width: 44, height: 44, color: '#10b981', margin: '0 auto 14px' }} />
            <p style={{ fontSize: 17, fontWeight: 800, color: '#10b981', margin: '0 0 6px' }}>No issues found!</p>
            <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>Your site's SEO looks great. Come back after publishing new content.</p>
          </div>
        )}

        {/* Empty state */}
        {!results && !polling && !loading && !error && (
          <div style={{ background: C.surface, border: `1.5px dashed ${C.border}`, borderRadius: 16, padding: '56px 32px', textAlign: 'center' }}>
            <div style={{ width: 52, height: 52, background: 'rgba(124,58,237,0.1)', border: `1px solid rgba(124,58,237,0.2)`, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Gauge weight="bold" style={{ width: 24, height: 24, color: C.violet }} />
            </div>
            <p style={{ fontSize: 16, fontWeight: 700, color: C.muted, margin: '0 0 6px' }}>Ready to audit your site</p>
            <p style={{ fontSize: 13, color: C.faint, margin: 0 }}>Enter your website URL above to detect SEO issues across all pages.</p>
          </div>
        )}
      </main>
    </div>
  );
}
